import { type ReactNode, useId, useRef } from 'react';

import { Text, YStack } from '@/components/ui';
import { Popover } from '@/components/ui/popover';

interface SharePopoverProps {
  anchor: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Position the popover against `anchor` without making it the trigger. For callers that open
   * the popover from elsewhere (e.g. a dropdown item) but want it anchored to a visible element.
   * Radix cannot restore focus to an anchor, so this mode restores it directly.
   */
  anchorOnly?: boolean;
}

export const SharePopover = ({
  anchor,
  title,
  description,
  children,
  open,
  onOpenChange,
  anchorOnly = false,
}: SharePopoverProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const closedByOutsideInteraction = useRef(false);
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      {anchorOnly ? (
        <Popover.Anchor ref={anchorRef} asChild>
          {anchor}
        </Popover.Anchor>
      ) : (
        <Popover.Trigger asChild>{anchor}</Popover.Trigger>
      )}
      <Popover.Content
        className="w-[21rem] px-4 pt-3.5 pb-0 overflow-hidden"
        align="end"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onInteractOutside={() => {
          closedByOutsideInteraction.current = true;
        }}
        onCloseAutoFocus={
          anchorOnly
            ? (event) => {
                if (!closedByOutsideInteraction.current) {
                  event.preventDefault();
                  anchorRef.current?.focus();
                }
                closedByOutsideInteraction.current = false;
              }
            : undefined
        }
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <YStack className="gap-0.5 mb-3">
          <Text id={titleId} size="sm" fontWeight="medium">
            {title}
          </Text>
          <Text id={descriptionId} size="xs" variant="muted">
            {description}
          </Text>
        </YStack>
        {children}
      </Popover.Content>
    </Popover.Root>
  );
};
